import { useState, useEffect, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import api from '../services/api';
import * as XLSX from 'xlsx';

// Helper to get API base URL - use environment variable or fallback to relative path
const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || '/api';
};

// Expected column headers (Row 1) - database column names
const EXPECTED_COLUMN_HEADERS = [
  'id',
  'vendorName',
  'requestReceivedAt',
  'clientNumber',
  'unitNumber',
  'vin',
  'registrationState',
  'transactionType',
  'requestor',
  'requestTrackingNumber',
  'estimatedCompleteAt',
  'status',
  'notes',
  'processingCompleteAt',
  'sentAgencyAt',
  'agencyTrackingNumber',
  'tempTagNumber',
  'tempTagExpirationAt',
  'licensePlateNumber',
  'licensePlateState',
  'licensePlateExpirationAt',
  'invoiceNumber'
];

// Note: Column descriptions (Row 2) are not validated as they are only for display purposes

interface ValidationError {
  message: string;
  details?: string[];
}

interface UploadResult {
  success: boolean;
  message: string;
  data?: {
    uploadId: string;
    blobName: string;
    status: string;
    message: string;
  };
  error?: string;
}

interface UploadHistoryItem {
  id: string;
  fileName: string;
  status: string;
  rowsInserted: number;
  rowsUpdated: number;
  rowsRejected: number;
  totalProcessed?: number;
  uploadedAt: string;
  processedAt?: string;
  agency: {
    id: string;
    name: string;
    code: string;
  };
  uploadedByUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface UploadHistoryResponse {
  data: UploadHistoryItem[];
  pagination: PaginationInfo;
}

interface ProcessingStatus {
  uploadId: string;
  fileName: string;
  status: 'uploaded' | 'processing' | 'completed' | 'error' | 'timeout' | 'completed_with_errors';
  message: string;
  uploadedAt: string;
  rowsInserted: number;
  rowsUpdated: number;
  rowsRejected: number;
  totalProcessed: number;
  agency: string;
  uploadedBy: string;
}

interface RejectedRecord {
  recordId: string;
  excelId: string | null;
  vin: string | null;
  vendorName: string | null;
  registrationState: string | null;
  licensePlateState: string | null;
  rejectionReasons: string[];
  rejectionCount: number;
  rejectionTimestamp: string | null;
  createdAt: string;
}

function Upload() {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [lastUploadId, setLastUploadId] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError | null>(null);
  const isPollingRef = useRef(false);
  const { user } = useUser();
  const isAgencyUser = user?.roles?.some(
    (r: any) => r.id === 'agency-user-role-id' || r.id === 'group-user-role-id'
  );

  // Upload history state
  const [uploadHistory, setUploadHistory] = useState<UploadHistoryItem[]>([]);
  const [uploadHistoryPagination, setUploadHistoryPagination] = useState<PaginationInfo>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [uploadHistoryLoading, setUploadHistoryLoading] = useState(false);
  const [uploadHistoryError, setUploadHistoryError] = useState<string | null>(null);

  // Rejected records modal state
  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const [rejectedRecords, setRejectedRecords] = useState<RejectedRecord[]>([]);
  const [rejectedRecordsLoading, setRejectedRecordsLoading] = useState(false);
  const [currentUploadHistoryId, setCurrentUploadHistoryId] = useState<string | null>(null);

  // Download template file
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/template/download', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Transactions_Template.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download template:', err);
      alert(err?.response?.data?.message || 'Failed to download template');
    }
  };

  // Download completed Excel from backend (served from Azure Blob)
  const handleDownloadCompletedFile = async (item: UploadHistoryItem) => {
    try {
      const response = await api.get(`/upload/history/download?uploadHistoryId=${item.id}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.fileName || 'download.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download completed file:', err);
      alert(err?.response?.data?.message || 'Failed to download file');
    }
  };

  // Load upload history
  const loadUploadHistory = async (page: number = uploadHistoryPagination.currentPage, pageSize: number = uploadHistoryPagination.pageSize) => {
    if (!user?.agency) return;

    setUploadHistoryLoading(true);
    setUploadHistoryError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });

      // If user is Agency User, restrict history to their own uploads only
      const isAgencyUser = user?.roles?.some(
        (r: any) => r.id === 'agency-user-role-id' || r.id === 'group-user-role-id'
      );
      if (isAgencyUser && user?.id) {
        params.append('uploadedBy', user.id);
      }

      const response = await api.get(`/upload/history?${params.toString()}`);
      const data: UploadHistoryResponse = response.data;

      // Client-side safeguard: if end user, display only their own uploads
      const isAgencyUserClient = user?.roles?.some(
        (r: any) => r.id === 'agency-user-role-id' || r.id === 'group-user-role-id'
      );
      const filteredRows = isAgencyUserClient && user?.id
        ? data.data.filter((row) => row.uploadedByUser?.id === user.id)
        : data.data;

      setUploadHistory(filteredRows);
      setUploadHistoryPagination(data.pagination);
    } catch (err: any) {
      console.error('Failed to load upload history:', err);
      setUploadHistoryError(err.response?.data?.message || 'Failed to load upload history');
    } finally {
      setUploadHistoryLoading(false);
    }
  };

  const handleUploadHistoryPageChange = (newPage: number) => {
    loadUploadHistory(newPage, uploadHistoryPagination.pageSize);
  };

  const handleUploadHistoryPageSizeChange = (newPageSize: number) => {
    setUploadHistoryPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
    loadUploadHistory(1, newPageSize);
  };

  // Load rejected records for a specific upload history
  const loadRejectedRecords = async (uploadHistoryId: string) => {
    setRejectedRecordsLoading(true);
    setShowRejectedModal(true);
    setCurrentUploadHistoryId(uploadHistoryId);

    try {
      const response = await api.get(`/upload/rejected-records?uploadHistoryId=${uploadHistoryId}`);
      
      // Handle different response structures
      let records = [];
      if (response.data) {
        if (response.data.success && Array.isArray(response.data.data)) {
          records = response.data.data;
        } else if (Array.isArray(response.data)) {
          // Handle case where data is directly an array
          records = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Handle case where success is missing but data exists
          records = response.data.data;
        }
      }
      
      setRejectedRecords(records);
    } catch (err: any) {
      console.error('Failed to load rejected records:', err);
      setRejectedRecords([]);
    } finally {
      setRejectedRecordsLoading(false);
    }
  };

  // Handle click on rejected count
  const handleRejectedClick = (uploadHistoryId: string, rejectedCount: number) => {
    if (rejectedCount > 0) {
      loadRejectedRecords(uploadHistoryId);
    }
  };

  // Handle export of rejected records
  const handleExportRejected = async () => {
    if (!currentUploadHistoryId) return;

    try {
      const response = await api.get(`/upload/rejected-records/export?uploadHistoryId=${currentUploadHistoryId}`, {
        responseType: 'blob'
      });

      // Create blob link and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'rejected_records_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to export rejected records:', err);
      alert(err.response?.data?.message || 'Failed to export rejected records');
    }
  };

  // Load upload history on component mount
  useEffect(() => {
    if (user?.agency) {
      loadUploadHistory();
    }
  }, [user]);

  // Manual refresh function
  const handleManualRefresh = async () => {
    if (uploadResult?.data?.uploadId) {
      console.log('Manual refresh triggered');
      await pollProcessingStatus(uploadResult.data.uploadId);
    }
  };

  // Validate Excel file structure (first row only - column headers)
  const validateExcelStructure = async (file: File): Promise<ValidationError | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            resolve({
              message: 'Excel file does not contain any worksheets',
            });
            return;
          }
          
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert worksheet to JSON array
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1, 
            defval: '',
            raw: false 
          }) as any[][];
          
          // Check if file has at least 1 row (column headers)
          if (jsonData.length < 1) {
            resolve({
              message: 'Excel file must have at least 1 row (column headers)',
              details: [`Found ${jsonData.length} row(s) instead of at least 1`]
            });
            return;
          }
          
          // Get first row (column headers)
          const firstRow = jsonData[0] || [];
          
          const errors: string[] = [];
          
          // Validate first row (column headers)
          if (firstRow.length !== EXPECTED_COLUMN_HEADERS.length) {
            errors.push(`Row 1 (Column Headers): Expected ${EXPECTED_COLUMN_HEADERS.length} columns, but found ${firstRow.length}`);
          } else {
            // Check each column header
            const missingHeaders: string[] = [];
            const wrongOrder: string[] = [];
            
            firstRow.forEach((header, index) => {
              const expectedHeader = EXPECTED_COLUMN_HEADERS[index];
              const headerStr = String(header || '').trim();
              
              if (headerStr !== expectedHeader) {
                if (EXPECTED_COLUMN_HEADERS.includes(headerStr)) {
                  wrongOrder.push(`Column ${index + 1}: Found "${headerStr}" but expected "${expectedHeader}"`);
                } else {
                  missingHeaders.push(`Column ${index + 1}: Expected "${expectedHeader}" but found "${headerStr}"`);
                }
              }
            });
            
            if (missingHeaders.length > 0) {
              errors.push(`Row 1 (Column Headers) - Missing or incorrect headers:`, ...missingHeaders);
            }
            if (wrongOrder.length > 0) {
              errors.push(`Row 1 (Column Headers) - Columns in wrong order:`, ...wrongOrder);
            }
          }
          
          if (errors.length > 0) {
            resolve({
              message: 'Excel file structure validation failed. The first row (column headers) does not match the expected template format.',
              details: errors
            });
            return;
          }
          
          // Validation passed
          resolve(null);
        } catch (error: any) {
          resolve({
            message: 'Failed to read Excel file',
            details: [error?.message || 'Unknown error occurred while reading the file']
          });
        }
      };
      
      reader.onerror = () => {
        resolve({
          message: 'Failed to read file',
          details: ['An error occurred while reading the file']
        });
      };
      
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // Validate file type
      if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
        alert('Please select an Excel file (.xlsx or .xls)');
        setValidationErrors(null);
        setFile(null);
        return;
      }
      
      // Clear previous validation errors
      setValidationErrors(null);
      
      // Validate Excel structure
      const validationError = await validateExcelStructure(selectedFile);
      
      if (validationError) {
        setValidationErrors(validationError);
        setFile(null);
        // Reset file input
        e.target.value = '';
        return;
      }
      
      // Validation passed
      setFile(selectedFile);
      setUploadResult(null); // Clear previous results
      setProcessingStatus(null); // Clear previous status
      setLastUploadId(null); // Clear last upload ID
      setValidationErrors(null); // Clear any previous errors
    }
  };

  // Poll for processing status
  const pollProcessingStatus = async (uploadId: string) => {
    try {
      console.log('Polling status for uploadId:', uploadId);
      // Use api client instead of fetch to include authentication headers
      // api client already has baseURL configured, so use relative path
      const response = await api.get(`upload/status?uploadId=${uploadId}&t=${Date.now()}&r=${Math.random()}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const result = response.data;
      
      console.log('Status response:', result);
      console.log('Response status code:', response.status);
      
      if (result && result.success) {
        console.log('Setting processing status to:', result.data);
        // Add timestamp to force re-render
        const statusWithTimestamp = {
          ...result.data,
          timestamp: new Date().toISOString()
        };
        setProcessingStatus(statusWithTimestamp);
        
        console.log('Current status:', result.data.status);
        console.log('Rows inserted:', result.data.rowsInserted);
        console.log('Rows rejected:', result.data.rowsRejected);
        console.log('Rows updated:', result.data.rowsUpdated);
        console.log('Timestamp:', statusWithTimestamp.timestamp);
        
        // Stop polling if processing is complete or failed
        if (result.data.status === 'completed' || result.data.status === 'completed_with_errors' || result.data.status === 'error' || result.data.status === 'timeout') {
          console.log('Processing completed, stopping polling');
          console.log('Setting isPolling to false');
          setIsPolling(false);
          isPollingRef.current = false;
        } else {
          console.log('Still processing, continuing to poll...');
        }
      } else {
        console.log('Status request failed:', result);
      }
    } catch (error: any) {
      console.error('Error polling status:', error);
      // Log more details for debugging
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
        console.error('Error response headers:', error.response.headers);
      }
      // Don't stop polling on network errors - might be temporary
      // Only stop on authentication errors (401, 403)
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.error('Authentication error - stopping polling');
        setIsPolling(false);
        isPollingRef.current = false;
      }
    }
  };

  // Debug: Watch for changes in processingStatus
  useEffect(() => {
    console.log('Processing status changed:', processingStatus);
    if (processingStatus?.status === 'completed' || processingStatus?.status === 'completed_with_errors' || processingStatus?.status === 'error' || processingStatus?.status === 'timeout') {
      console.log('Status is completed/error/timeout, stopping polling');
      setIsPolling(false);
      isPollingRef.current = false;
      // Force a re-render by updating a dummy state
      setForceRefresh(prev => prev + 1);
      // Refresh upload history so the latest processed upload appears at the top
      loadUploadHistory(1, uploadHistoryPagination.pageSize);
    } else if (processingStatus?.status === 'processing') {
      console.log('Status is processing, ensuring polling continues');
      // Don't stop polling for processing status
    }
  }, [processingStatus]);

  // Debug: Watch for changes in isPolling
  useEffect(() => {
    console.log('isPolling changed to:', isPolling);
  }, [isPolling]);

  // Start polling when upload is successful
  useEffect(() => {
    console.log('Upload useEffect triggered:', { uploadResult: uploadResult?.success, uploadId: uploadResult?.data?.uploadId, isPolling, lastUploadId });
    if (uploadResult?.success && uploadResult.data?.uploadId && !isPolling && uploadResult.data.uploadId !== lastUploadId) {
      console.log('Starting polling for uploadId:', uploadResult.data.uploadId);
      setLastUploadId(uploadResult.data.uploadId);
      setIsPolling(true);
      isPollingRef.current = true;
      const uploadId = uploadResult.data.uploadId;
      let pollCount = 0;
      
      // Poll every 3 seconds
      const interval = setInterval(async () => {
        console.log('Polling interval triggered, isPollingRef:', isPollingRef.current);
        if (isPollingRef.current) {
          pollCount++;
          console.log('Poll count:', pollCount);
          
          // Removed manual processing backup trigger to prevent duplicate processing
          // The blob trigger should handle processing. If it times out, we'll just continue polling
          // or show an error message to the user instead of triggering duplicate processing.
          
          // Continue polling even after manual processing is triggered
          // Only stop if we've exceeded a very long timeout (e.g., 5 minutes)
          if (pollCount >= 100) { // 100 polls * 3 seconds = 5 minutes absolute timeout
            console.log('Absolute timeout reached - stopping polling');
            setIsPolling(false);
            isPollingRef.current = false;
            clearInterval(interval);
            return;
          }
          
          pollProcessingStatus(uploadId);
        } else {
          console.log('Polling stopped, clearing interval');
          clearInterval(interval);
        }
      }, 3000);
      
      // Initial poll
      pollProcessingStatus(uploadId);
      
      // Cleanup function to clear interval when component unmounts or polling stops
      return () => {
        console.log('Cleaning up polling interval');
        clearInterval(interval);
      };
    }
  }, [uploadResult]);

  const handleUpload = async () => {
    if (!file || !user) return;
    
    setUploading(true);
    setUploadResult(null);
    setProcessingStatus(null);

    try {
      // Step 1: Convert file to base64
      const fileData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove the data URL prefix to get just the base64 data
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Step 2: Upload file through Azure Functions (avoids CORS issues)
      const uploadResponse = await fetch(`${getApiBaseUrl()}/upload/file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agencyId: user.agency.id,
          fileName: file.name,
          fileData,
        }),
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      const uploadResult = await uploadResponse.json();
      const blobName = uploadResult.blobName;
      const originalFileName = uploadResult.originalFileName || file.name; // Use original filename from response

      // Step 3: Record the upload and trigger processing
      const processResponse = await fetch(`${getApiBaseUrl()}/upload/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          blobName,
          agencyId: user.agency.id,
          uploadedBy: user.id,
          originalFileName, // Pass original filename for upload history
        }),
      });

      const result: UploadResult = await processResponse.json();
      setUploadResult(result);

      if (result.success) {
        setFile(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadResult({
        success: false,
        message: 'Upload failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <h1 style={{ marginBottom: '8px' }}>Upload Transactions</h1>
      <p style={{ 
        color: '#6c757d', 
        marginBottom: '32px',
        fontSize: '1.1rem'
      }}>
        Upload Excel files using the standard template format
      </p>
      
      <div className="card" style={{ marginBottom: '32px' }}>
        <div style={{ 
          border: '2px dashed #ccc', 
          borderRadius: '8px', 
          padding: '24px', 
          backgroundColor: '#f8f9fa'
        }}>
          {/* Header with icon and instructions */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <div style={{ 
              fontSize: '2.5rem', 
              marginRight: '16px',
              opacity: showTooltip ? 0.8 : 0.5,
              position: 'relative',
              display: 'inline-block',
              cursor: 'help',
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            >
              <span className="material-symbols-outlined" style={{ 
                fontSize: '2.5rem', 
                opacity: showTooltip ? 0.8 : 0.5,
                color: 'var(--secondary-color)'
              }}>
                cloud_upload
              </span>
              
              {/* Tooltip */}
              {showTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '100%',
                  transform: 'translateY(-50%)',
                  marginLeft: '12px',
                  backgroundColor: '#333',
                  color: 'white',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  width: '363px',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  lineHeight: '1.4'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#fff' }}>
                    <span className="material-symbols-outlined" style={{ 
                      fontSize: '16px', 
                      opacity: 0.8,
                      marginRight: '6px',
                      verticalAlign: 'middle'
                    }}>
                      description
                    </span>
                    Upload Instructions
                  </div>
                  <ol style={{ paddingLeft: '16px', margin: '0 0 8px 0' }}>
                    <li style={{ marginBottom: '4px' }}>
                      Download the standard template from the system
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                      Fill in your data following the template format
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                      Leave the ID column empty for new records
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                      Include an ID value to update existing records
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                      Ensure VIN numbers are provided for all records
                    </li>
                  </ol>
                  <div style={{ 
                    backgroundColor: 'rgba(255,255,255,0.1)', 
                    padding: '8px', 
                    borderRadius: '4px',
                    fontSize: '0.8rem'
                  }}>
                    <strong>
                      <span className="material-symbols-outlined" style={{ 
                        fontSize: '14px', 
                        opacity: 0.8,
                        marginRight: '4px',
                        verticalAlign: 'middle'
                      }}>
                        lightbulb
                      </span>
                      Tip:
                    </strong> The first row contains column names, and the second row contains 
                    user-friendly descriptions. Data should start from the third row.
                  </div>
                  
                  {/* Tooltip arrow pointing left */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-6px',
                    transform: 'translateY(-50%)',
                    width: '0',
                    height: '0',
                    borderTop: '6px solid transparent',
                    borderBottom: '6px solid transparent',
                    borderRight: '6px solid #333'
                  }}></div>
                </div>
              )}
            </div>
            
            <div style={{ textAlign: 'left' }}>
              <h3 style={{ margin: '0 0 8px 0', color: 'var(--secondary-color)' }}>
                Upload & Process
              </h3>
              <p style={{ margin: '0', fontSize: '0.9rem', color: '#666' }}>
                <span className="material-symbols-outlined" style={{ 
                  fontSize: '16px', 
                  opacity: 0.6,
                  marginRight: '4px',
                  verticalAlign: 'middle'
                }}>
                  lightbulb
                </span>
                Hover over the upload icon for instructions
              </p>
            </div>
          </div>

          {/* Horizontal layout for file input and upload button */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {/* File input */}
            <div style={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ 
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  fontSize: '0.9rem'
                }}
                disabled={uploading}
              />
            </div>

            {/* Upload button */}
            <div>
              <button 
                onClick={handleUpload} 
                disabled={uploading || !file}
                style={{ 
                  padding: '12px 24px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  opacity: (uploading || !file) ? 0.6 : 1,
                  cursor: (uploading || !file) ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: uploading || !file ? 'rgba(0, 0, 0, 0.1)' : 'none',
                  border: uploading || !file ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid #FC5003',
                  color: uploading || !file ? 'rgba(0, 0, 0, 0.4)' : '#FC5003',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!uploading && file) {
                    e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!uploading && file) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span className="material-symbols-outlined">
                  {uploading ? 'hourglass_empty' : 'cloud_upload'}
                </span>
                {uploading ? 'Processing...' : 'Upload & Process'}
              </button>
            </div>
          </div>

          {/* Validation Errors */}
          {validationErrors && (
            <div style={{ 
              marginTop: '16px',
              padding: '16px', 
              backgroundColor: '#f8d7da', 
              borderRadius: '6px',
              border: '1px solid #f5c6cb',
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '12px'
              }}>
                <span className="material-symbols-outlined" style={{ 
                  fontSize: '24px', 
                  color: '#dc3545'
                }}>
                  error
                </span>
                <h4 style={{ 
                  margin: 0, 
                  color: '#721c24',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}>
                  File Validation Failed
                </h4>
              </div>
              <p style={{ 
                margin: '0 0 12px 0', 
                color: '#721c24',
                fontSize: '0.95rem'
              }}>
                {validationErrors.message}
              </p>
              {validationErrors.details && validationErrors.details.length > 0 && (
                <div style={{
                  backgroundColor: '#fff',
                  border: '1px solid #f5c6cb',
                  borderRadius: '4px',
                  padding: '12px',
                  marginTop: '12px'
                }}>
                  <div style={{ 
                    fontWeight: '600', 
                    marginBottom: '8px', 
                    color: '#721c24',
                    fontSize: '0.9rem'
                  }}>
                    Validation Details:
                  </div>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '20px', 
                    color: '#721c24',
                    fontSize: '0.9rem',
                    lineHeight: '1.6'
                  }}>
                    {validationErrors.details.map((detail, index) => (
                      <li key={index} style={{ marginBottom: '4px' }}>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={{ 
                marginTop: '12px', 
                padding: '8px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                fontSize: '0.85rem',
                color: '#856404'
              }}>
                <strong>Tip:</strong> Please download the{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadTemplate();
                  }}
                  style={{
                    color: '#856404',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#721c24';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#856404';
                  }}
                >
                  standard template
                </a>
                {' '}and ensure your file matches the expected format.
              </div>
            </div>
          )}

          {/* File info display */}
          {file && (
            <div style={{ 
              marginTop: '16px',
              padding: '12px', 
              backgroundColor: '#e7f3ff', 
              borderRadius: '6px',
              border: '1px solid #b3d9ff',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                Selected File: {file.name}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                Size: {(file.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>
          )}

          {/* Instructions when no file selected */}
          {!file && (
            <div style={{ 
              marginTop: '16px',
              textAlign: 'center',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#666' }}>
                Click to select an Excel file (.xlsx or .xls)
              </p>
              <p style={{ margin: '0', fontSize: '0.85rem', color: '#999' }}>
                Make sure your file follows the standard{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadTemplate();
                  }}
                  style={{
                    color: '#015F93',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#FC5003';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#015F93';
                  }}
                >
                  template format
                </a>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Results */}
      {uploadResult && (
        <div className="card">
          <h3 style={{ 
            color: uploadResult.success ? 'var(--success-color, #28a745)' : 'var(--error-color, #dc3545)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span className="material-symbols-outlined" style={{ 
              fontSize: '24px', 
              opacity: 0.7,
              color: uploadResult.success ? '#28a745' : '#dc3545'
            }}>
              {uploadResult.success ? 'check_circle' : 'error'}
            </span>
            {uploadResult.success ? 'File Uploaded Successfully' : 'Upload Failed'}
          </h3>
          
          <p style={{ marginBottom: '16px' }}>
            {uploadResult.message}
          </p>

          {uploadResult.error && (
            <div style={{ 
              backgroundColor: '#f8d7da', 
              border: '1px solid #f5c6cb', 
              borderRadius: '6px', 
              padding: '12px',
              color: '#721c24',
              marginBottom: '16px'
            }}>
              <strong>Error:</strong> {uploadResult.error}
            </div>
          )}
        </div>
      )}

      {/* Processing Status */}
      {processingStatus && (
        <div key={`${forceRefresh}-${(processingStatus as any).timestamp || Date.now()}`} className="card">
          <h3 style={{ 
            color: processingStatus.status === 'completed' ? '#28a745' : 
                   processingStatus.status === 'completed_with_errors' ? '#ffc107' :
                   processingStatus.status === 'error' ? '#dc3545' : 
                   processingStatus.status === 'timeout' ? '#ffc107' : '#007bff',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span className="material-symbols-outlined" style={{ 
              fontSize: '24px',
              color: processingStatus.status === 'completed' ? '#28a745' :
                     processingStatus.status === 'completed_with_errors' ? '#ffc107' :
                     processingStatus.status === 'error' ? '#dc3545' :
                     processingStatus.status === 'timeout' ? '#ff9800' : '#007bff',
              marginRight: '8px'
            }}>
              {processingStatus.status === 'completed' ? 'check_circle' :
               processingStatus.status === 'completed_with_errors' ? 'warning' :
               processingStatus.status === 'error' ? 'error' :
               processingStatus.status === 'timeout' ? 'schedule' : 'hourglass_empty'}
            </span>
            {processingStatus.status === 'completed' ? 'Processing Completed' :
             processingStatus.status === 'completed_with_errors' ? 'Processing Completed with Errors' :
             processingStatus.status === 'error' ? 'Processing Failed' :
             processingStatus.status === 'timeout' ? 'Processing Timeout' : 'Processing File...'}
          </h3>
          
          <button 
            onClick={handleManualRefresh}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: 'none',
              border: '1px solid #015F93',
              color: '#015F93',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '16px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span className="material-symbols-outlined small">refresh</span>
            Refresh Status
          </button>
          
          <p style={{ marginBottom: '16px' }}>
            {processingStatus.message}
          </p>

          {(processingStatus.status === 'completed' || processingStatus.status === 'completed_with_errors') && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '16px', 
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              <h4 style={{ marginBottom: '12px', color: 'var(--secondary-color)' }}>
                Processing Summary
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '12px' 
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                    {processingStatus.rowsInserted}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>New Records</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                    {processingStatus.rowsUpdated}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>Updated Records</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div 
                    style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold', 
                      color: '#ffc107',
                      cursor: processingStatus.rowsRejected > 0 ? 'pointer' : 'default',
                      textDecoration: processingStatus.rowsRejected > 0 ? 'underline' : 'none'
                    }}
                    onClick={() => processingStatus.rowsRejected > 0 && handleRejectedClick(processingStatus.uploadId, processingStatus.rowsRejected)}
                    title={processingStatus.rowsRejected > 0 ? 'Click to view rejected records' : ''}
                  >
                    {processingStatus.rowsRejected}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>Rejected Records</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6c757d' }}>
                    {processingStatus.totalProcessed}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>Total Processed</div>
                </div>
              </div>
            </div>
          )}

          {processingStatus.status === 'processing' && (
            <div style={{ 
              backgroundColor: '#e7f3ff', 
              border: '1px solid #b3d9ff', 
              borderRadius: '6px', 
              padding: '12px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  border: '2px solid #007bff', 
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span style={{ color: '#004085' }}>
                  Processing large file... This may take a few minutes.
                </span>
              </div>
            </div>
          )}

          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '12px', 
            borderRadius: '6px',
            fontSize: '0.9rem',
            color: '#666'
          }}>
            <div><strong>File:</strong> {processingStatus.fileName}</div>
            <div><strong>Uploaded:</strong> {new Date(processingStatus.uploadedAt).toLocaleString()}</div>
            <div><strong>By:</strong> {processingStatus.uploadedBy}</div>
            <div><strong>Agency:</strong> {processingStatus.agency}</div>
          </div>
        </div>
      )}

      {/* Upload History */}
      <div style={{ marginTop: '48px' }}>
        <h2 style={{ marginBottom: '8px' }}></h2>
        <p style={{ 
          color: '#6c757d', 
          marginBottom: '32px',
          fontSize: '1.1rem'
        }}>
          View your recent uploads and their processing status
        </p>
        
        <div className="card">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, color: 'var(--secondary-color)' }}>
              Upload History ({isAgencyUser ? uploadHistory.length : uploadHistoryPagination.totalCount})
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ fontSize: '14px', color: 'var(--secondary-color)' }}>
                Rows per page:
              </label>
              <select
                value={uploadHistoryPagination.pageSize}
                onChange={(e) => handleUploadHistoryPageSizeChange(parseInt(e.target.value))}
                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {uploadHistoryError && (
            <div style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {uploadHistoryError}
            </div>
          )}

          {uploadHistoryLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--secondary-color)' }}>
              Loading...
            </div>
          ) : uploadHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
              No upload history found
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ 
                      backgroundColor: 'var(--background-color)', 
                      borderBottom: '1px solid #e0e0e0' 
                    }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>File Name</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>New Records</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Updated Records</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Rejected Records</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Uploaded By</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Uploaded At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadHistory.map((item, index) => (
                      <tr key={item.id} style={{ 
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: index % 2 === 0 ? 'white' : 'rgba(0, 0, 0, 0.02)'
                      }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: 500 }}>{item.fileName}</span>
                            {item.status === 'completed' && (
                              <button
                                onClick={() => handleDownloadCompletedFile(item)}
                                title="Download completed file"
                                aria-label="Download completed file"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: '#015F93',
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>download</span>
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: item.status === 'completed' ? '#d4edda' : 
                                           item.status === 'completed_with_errors' ? '#fff3cd' :
                                           item.status === 'error' ? '#f8d7da' :
                                           item.status === 'processing' ? '#cce5ff' : '#e9ecef',
                            color: item.status === 'completed' ? '#155724' : 
                                   item.status === 'completed_with_errors' ? '#856404' :
                                   item.status === 'error' ? '#721c24' :
                                   item.status === 'processing' ? '#004085' : '#495057'
                          }}>
                            <span className="material-symbols-outlined" style={{ 
                              fontSize: '16px', 
                              opacity: 0.7,
                              color: item.status === 'completed' ? '#28a745' : 
                                     item.status === 'completed_with_errors' ? '#ffc107' :
                                     item.status === 'error' ? '#dc3545' :
                                     item.status === 'processing' ? '#007bff' : '#6c757d'
                            }}>
                              {item.status === 'completed' ? 'check_circle' :
                               item.status === 'completed_with_errors' ? 'warning' :
                               item.status === 'error' ? 'error' :
                               item.status === 'processing' ? 'hourglass_empty' : 'cloud_upload'}
                            </span>
                            <span>
                              {item.status === 'completed' ? 'Completed' :
                               item.status === 'completed_with_errors' ? 'Completed with Errors' :
                               item.status === 'error' ? 'Error' :
                               item.status === 'processing' ? 'Processing' : 'Uploaded'}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ 
                            color: '#28a745', 
                            fontWeight: '500' 
                          }}>
                            {item.rowsInserted}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ 
                            color: '#007bff', 
                            fontWeight: '500' 
                          }}>
                            {item.rowsUpdated}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span 
                            style={{ 
                              color: '#ffc107', 
                              fontWeight: '500',
                              cursor: item.rowsRejected > 0 ? 'pointer' : 'default',
                              textDecoration: item.rowsRejected > 0 ? 'underline' : 'none'
                            }}
                            onClick={() => item.rowsRejected > 0 && handleRejectedClick(item.id, item.rowsRejected)}
                            title={item.rowsRejected > 0 ? 'Click to view rejected records' : ''}
                          >
                            {item.rowsRejected}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          {item.uploadedByUser.firstName} {item.uploadedByUser.lastName}
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6c757d' }}>
                          {new Date(item.uploadedAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: '1px solid #e0e0e0'
              }}>
                <div style={{ color: '#6c757d', fontSize: '14px' }}>
                  {isAgencyUser ? (
                    <>Showing 1 to {uploadHistory.length} of {uploadHistory.length} entries</>
                  ) : (
                    <>Showing {((uploadHistoryPagination.currentPage - 1) * uploadHistoryPagination.pageSize) + 1} to {Math.min(uploadHistoryPagination.currentPage * uploadHistoryPagination.pageSize, uploadHistoryPagination.totalCount)} of {uploadHistoryPagination.totalCount} entries</>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleUploadHistoryPageChange(uploadHistoryPagination.currentPage - 1)}
                    disabled={isAgencyUser || !uploadHistoryPagination.hasPreviousPage}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '14px',
                      opacity: (isAgencyUser || !uploadHistoryPagination.hasPreviousPage) ? 0.6 : 1,
                      cursor: (isAgencyUser || !uploadHistoryPagination.hasPreviousPage) ? 'not-allowed' : 'pointer',
                      padding: '8px 12px',
                      background: (isAgencyUser || !uploadHistoryPagination.hasPreviousPage) ? 'rgba(0, 0, 0, 0.1)' : 'none',
                      border: (isAgencyUser || !uploadHistoryPagination.hasPreviousPage) ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid #015F93',
                      color: (isAgencyUser || !uploadHistoryPagination.hasPreviousPage) ? 'rgba(0, 0, 0, 0.4)' : '#015F93',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isAgencyUser && uploadHistoryPagination.hasPreviousPage) {
                        e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isAgencyUser && uploadHistoryPagination.hasPreviousPage) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span className="material-symbols-outlined small">chevron_left</span>
                    Previous
                  </button>
                  <span style={{ 
                    padding: '8px 12px', 
                    color: '#6c757d',
                    fontSize: '14px'
                  }}>
                    {isAgencyUser ? (
                      <>Page 1 of 1</>
                    ) : (
                      <>Page {uploadHistoryPagination.currentPage} of {uploadHistoryPagination.totalPages}</>
                    )}
                  </span>
                  <button
                    onClick={() => handleUploadHistoryPageChange(uploadHistoryPagination.currentPage + 1)}
                    disabled={isAgencyUser || !uploadHistoryPagination.hasNextPage}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '14px',
                      opacity: (isAgencyUser || !uploadHistoryPagination.hasNextPage) ? 0.6 : 1,
                      cursor: (isAgencyUser || !uploadHistoryPagination.hasNextPage) ? 'not-allowed' : 'pointer',
                      padding: '8px 12px',
                      background: (isAgencyUser || !uploadHistoryPagination.hasNextPage) ? 'rgba(0, 0, 0, 0.1)' : 'none',
                      border: (isAgencyUser || !uploadHistoryPagination.hasNextPage) ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid #015F93',
                      color: (isAgencyUser || !uploadHistoryPagination.hasNextPage) ? 'rgba(0, 0, 0, 0.4)' : '#015F93',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isAgencyUser && uploadHistoryPagination.hasNextPage) {
                        e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isAgencyUser && uploadHistoryPagination.hasNextPage) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    Next
                    <span className="material-symbols-outlined small">chevron_right</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rejected Records Modal */}
      {showRejectedModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => {
            setShowRejectedModal(false);
            setCurrentUploadHistoryId(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: 'var(--secondary-color)' }}>
                Rejected Records ({rejectedRecords.length})
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {rejectedRecords.length > 0 && (
                  <button
                    onClick={handleExportRejected}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      background: 'none',
                      border: '1px solid #28a745',
                      color: '#28a745',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(40, 167, 69, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Export rejected records to Excel"
                    aria-label="Export to Excel"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>download</span>
                    Export
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowRejectedModal(false);
                    setCurrentUploadHistoryId(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{
              padding: '20px',
              overflowY: 'auto',
              flex: 1
            }}>
              {rejectedRecordsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--secondary-color)' }}>
                  Loading rejected records...
                </div>
              ) : rejectedRecords.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  No rejected records found
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {rejectedRecords.map((record, index) => (
                    <div
                      key={record.recordId}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        padding: '16px',
                        backgroundColor: '#fff9e6'
                      }}
                    >
                      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px', color: '#333' }}>
                            Record #{index + 1}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.excelId && `Excel ID: ${record.excelId}`}
                            {record.excelId && record.vin && ' • '}
                            {record.vin && `VIN: ${record.vin}`}
                          </div>
                          {record.vendorName && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                              Vendor: {record.vendorName}
                            </div>
                          )}
                          {(record.registrationState || record.licensePlateState) && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                              {record.registrationState && `Registration State: ${record.registrationState}`}
                              {record.registrationState && record.licensePlateState && ' • '}
                              {record.licensePlateState && `License Plate State: ${record.licensePlateState}`}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: '#999' }}>
                          {new Date(record.createdAt).toLocaleString()}
                        </div>
                      </div>
                      
                      <div style={{
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '4px',
                        padding: '12px',
                        marginTop: '12px'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '8px', color: '#856404', fontSize: '13px' }}>
                          Rejection Reasons ({record.rejectionCount}):
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404', fontSize: '13px' }}>
                          {record.rejectionReasons.map((reason, reasonIndex) => (
                            <li key={reasonIndex} style={{ marginBottom: '4px' }}>
                              {reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowRejectedModal(false);
                  setCurrentUploadHistoryId(null);
                }}
                style={{
                  padding: '10px 24px',
                  background: '#015F93',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#014d75';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#015F93';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Upload;


